// src/components/ServiceWorkerUpdate.jsx
// Simple, mobile-friendly update notification
import { useState, useEffect } from 'react'
import { fixedServiceWorkerManager } from '../lib/fixedServiceWorkerUtils'

const ServiceWorkerUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    // Listen for update available event
    const handleUpdateAvailable = (event) => {
      // Check if we're in cooldown (just updated)
      const state = fixedServiceWorkerManager.getState()
      if (state.inCooldown) {
        console.log('[ServiceWorkerUpdate] Update event received but in cooldown, ignoring')
        return
      }
      setUpdateAvailable(true)
    }

    // Listen for stale deployment event (chunk 404 detected)
    const handleStaleDeployment = () => {
      console.warn('[ServiceWorkerUpdate] Stale deployment detected, showing update prompt')
      setUpdateAvailable(true)
    }

    window.addEventListener('sw-update-available', handleUpdateAvailable)
    window.addEventListener('sw-stale-deployment', handleStaleDeployment)

    // Check initial state
    const state = fixedServiceWorkerManager.getState()
    if (state.updateAvailable && !state.inCooldown) {
      setUpdateAvailable(true)
    }

    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable)
      window.removeEventListener('sw-stale-deployment', handleStaleDeployment)
    }
  }, [])

  const handleUpdate = async () => {
    // Hide the popup synchronously so any re-fired sw-update-available events
    // during the reload window don't flip it back on.
    setUpdateAvailable(false)
    setIsUpdating(true)

    // Set the cooldown BEFORE any async work, so it sticks even if there is no
    // waiting worker for skipWaiting() to message.
    fixedServiceWorkerManager.markJustUpdated()

    try {
      // The SW's activate handler purges old caches itself. Clearing caches
      // here races with in-flight lazy-route chunk requests and can break
      // navigation that happens between this call and the reload.
      await fixedServiceWorkerManager.skipWaiting()

      setTimeout(() => {
        window.location.reload()
      }, 500)
    } catch (error) {
      console.error('Failed to update:', error)
      window.location.reload()
    }
  }

  // Don't show anything if no update available
  if (!updateAvailable) {
    return null
  }

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-xs z-50"
      style={{
        // Ensure proper positioning on iOS PWA with safe areas
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
    >
      <div className="bg-elevated text-primary p-4 rounded-xl shadow-2xl border border-mute">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-base text-primary">New Update</h3>
            <p className="text-sm text-secondary">A new version is available</p>
          </div>
        </div>

        <button
          onClick={handleUpdate}
          disabled={isUpdating}
          className="mt-3 w-full px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg
                     text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors active:scale-[0.98] touch-manipulation"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          {isUpdating ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Updating...
            </span>
          ) : (
            'Update'
          )}
        </button>
      </div>
    </div>
  )
}

export default ServiceWorkerUpdate
